<template>
  <div>
    <div>
      <div class="row">
        <div class="col-6 col-md-2">
          <base-input>
            <el-select
              class="select-primary pagination-select"
              v-model="pagination.perPage"
              :placeholder="$t('COMMON.PER_PAGE')"
            >
              <el-option
                class="select-primary"
                v-for="item in pagination.perPageOptions"
                :key="item"
                :label="item"
                :value="item"
              >
              </el-option>
            </el-select>
          </base-input>
        </div>

        <div class="col-6 col-md-2">
          <base-input>
            <category-group-selector
              :allowNone="false"
              :showAll="false"
              :isOnlyModels="false"
              :placeholder="$t('COMMON.CATEGORIES')"
              @categoryChanged="
                (categoryId) => {
                  selectedCategory = categoryId;
                }
              "
            />
          </base-input>
        </div>

        <div class="col-4 col-md-2">
          <base-input>
            <supplier-selector
              v-if="$currentUserCan($permissions.PERM_VIEW_ANY_SUPPLIERS)"
              :filterOrganization="warehouse.organization.id"
              :supplier="selectedSupplier"
              @supplierChanged="(supplierId) => (selectedSupplier = supplierId)"
            />
          </base-input>
        </div>

        <div class="col-6 col-md-2">
          <base-input
            v-model="query"
            type="search"
            prepend-icon="fas fa-search"
            :placeholder="$t('COMMON.SEARCH_HINT.PRODUCT_NAME_OR_SKU')"
            clearable
          />
        </div>

        <div class="col-auto d-flex align-items-center">
          <base-checkbox v-model="filterCustomPricing">
            {{ $t("COMMON.CUSTOM_PRICE") }}
          </base-checkbox>
        </div>

        <div class="col-auto ml-auto">
          <base-button
            type="primary"
            size="sm"
            @click="exportToExcel"
            :disabled="exporting"
          >
            <i :class="exporting ? 'fas fa-spinner fa-spin' : 'fa fa-file-excel'"></i>
            {{ $t("COMMON.EXPORT_EXCEL") }}
          </base-button>
        </div>
      </div>

      <el-table
        class="table-responsive align-items-center table-flush sales-pricing-list"
        header-row-class-name="thead-light"
        :data="warehouseProducts"
        row-key="id"
        @sort-change="sortChange"
      >
        <div slot="empty" v-if="loading">
          <img src="/img/loading.gif" style="height: 100px; width: 100px" />
        </div>

        <el-table-column
          :label="$t('COMMON.PRODUCT')"
          prop="product.name"
          min-width="350px"
          sortable="custom"
        >
          <template v-slot="{ row }">
            <object-link
              v-if="row.product"
              :object="row.product"
            />
              <el-tooltip
                :content="$t('COMMON.CUSTOM_PRICE')"
                placement="top"
                v-if="warehouseProductsModel[row.id].custom_selling_price"
              >
                <i class="fas fa-tag text-warning ml-2" style="font-size: 1.2em;"></i>
              </el-tooltip>
          </template>
        </el-table-column>

        <el-table-column
          :label="$t('PRODUCTS.SKU')"
          prop="sku"
          min-width="200px"
        >
          <template v-slot="{ row }">
            {{ row.sku || row.product?.sku }}
          </template>
        </el-table-column>

        <el-table-column
          :label="$t('COMMON.SELLING_PRICE')"
          prop="selling_price"
          min-width="100px"
          sortable="custom"
        >
          <template v-slot="{ row }">
            <div class="d-flex align-items-center">
              <base-input
                v-model="warehouseProductsModel[row.id].selling_price"
                input-classes="form-control-alternative"
                @change="onPriceChange(row.id)"
                :disabled="!canEditProducts"
                type="number"
                step="0.01"
              />
            </div>
          </template>
        </el-table-column>

        <el-table-column
          :label="$t('COMMON.SUBSIDIZED_PRICE')"
          prop="subsidized_price"
          min-width="100px"
        >
          <template v-slot="{ row }">
            <div class="d-flex align-items-center">
              <base-input
                v-model="warehouseProductsModel[row.id].subsidized_price"
                input-classes="form-control-alternative"
                @change="onSubsidizedPriceChange(row.id)"
                :disabled="!canEditProducts"
                type="number"
                step="0.01"
              />
            </div>
          </template>
        </el-table-column>

        <el-table-column
          :label="$t('COMMON.CATERER_PRICE')"
          prop="caterer_price"
          min-width="100px"
        >
          <template v-slot="{ row }">
            <div class="d-flex align-items-center">
              <base-input
                v-model="warehouseProductsModel[row.id].caterer_price"
                input-classes="form-control-alternative"
                @change="onCatererPriceChange(row.id)"
                :disabled="!canEditProducts"
                type="number"
                step="0.01"
              />
            </div>
          </template>
        </el-table-column>

        <el-table-column align="center" min-width="80px">
          <template slot-scope="{ row }">
            <el-tooltip
              content="Loading"
              placement="top"
              v-if="warehouseProductsModel[row.id].loading"
            >
              <i class="fas fa-spinner fa-spin"></i>
            </el-tooltip>

            <el-tooltip
              :content="$t('PRODUCTS.RESET_TO_DEFAULT_PRICE')"
              placement="top"
              v-if="warehouseProductsModel[row.id].custom_selling_price && !warehouseProductsModel[row.id].loading && canEditProducts"
            >
              <a
                type="text"
                @click="resetCustomPricing(row.id)"
                class="table-action text-danger"
                style="cursor: pointer"
              >
                <i class="fas fa-undo"></i>
              </a>
            </el-tooltip>
          </template>
        </el-table-column>
      </el-table>
    </div>

    <div
      slot="footer"
      class="mt-5 col-12 d-flex justify-content-center justify-content-sm-between flex-wrap table-footer"
    >
      <div class="">
        <p class="card-category">
          {{
            $t("COMMON.DISPLAY_FROM_X_TO_X_OF_X_ENTRIES", {
              from: total ? from + 1 : 0,
              to: to,
              of: total,
            })
          }}
        </p>
      </div>
      <base-pagination
        class="pagination-no-border"
        v-model="pagination.currentPage"
        :per-page="pagination.perPage"
        :total="total"
      />
    </div>
  </div>
</template>

<script>
import _ from "lodash";
import { mapGetters } from "vuex";
import {
  Option,
  Select,
  Table,
  TableColumn,
  Tooltip,
} from "element-ui";
import requestErrorMixin from "@/mixins/request-error-mixin";
import { OPTION_FORCE_MODEL_USAGE } from "@/constants/options";
import SupplierSelector from "@/components/SupplierSelector.vue";
import CategoryGroupSelector from "@/components/CategoryGroupSelector.vue";

export default {
  name: "warehouse-sales-pricing-list",

  components: {
    [Tooltip.name]: Tooltip,
    [Table.name]: Table,
    [TableColumn.name]: TableColumn,
    [Select.name]: Select,
    [Option.name]: Option,
    SupplierSelector,
    CategoryGroupSelector,
  },

  mixins: [requestErrorMixin],

  props: {
    warehouse: {
      type: Object,
      required: true,
      description: "Warehouse",
    },
  },

  created() {
    if (this.warehouse) {
      this.$store.dispatch(
        "organizationOptions/getOrganizationConfig",
        this.warehouse.organization.id
      );
    }
  },

  data() {
    return {
      query: null,
      sort: "product.name",
      total: 0,
      pagination: {
        perPage: 20,
        currentPage: 1,
        perPageOptions: [20, 50, 100, 500],
      },
      warehouseProducts: [],
      loading: true,
      selectedSupplier: null,
      warehouseProductsModel: {},
      selectedCategory: null,
      exporting: false,
      filterCustomPricing: false,
    };
  },

  computed: {
    from() {
      return this.pagination.perPage * (this.pagination.currentPage - 1);
    },

    to() {
      let highBound = this.from + this.pagination.perPage;
      if (this.total < highBound) {
        highBound = this.total;
      }
      return highBound;
    },

    ...mapGetters({
      organizationConfig: "organizationOptions/organizationConfig",
    }),

    forceModelUsage() {
      return this.organizationConfig[OPTION_FORCE_MODEL_USAGE];
    },

    canEditProducts() {
      if (!this.forceModelUsage) {
        return this.$currentUserCan(this.$permissions.PERM_EDIT_PRODUCTS);
      }
      return !!this.warehouse?.is_model && this.$currentUserCan(this.$permissions.PERM_EDIT_PRODUCTS);
    },
  },

  watch: {
    pagination: {
      handler: "getListDebounced",
      immediate: true,
      deep: true,
    },
    query: {
      handler: "getListDebounced",
      immediate: true,
    },
    selectedSupplier: {
      handler: "getListDebounced",
      immediate: true,
    },
    warehouseProducts: {
      handler: "refreshWarehouseProducts",
      immediate: true,
    },
    selectedCategory: {
      handler: "getListDebounced",
      immediate: true,
    },
    filterCustomPricing: {
      handler: "getListDebounced",
      immediate: false,
    },
  },

  methods: {
    getListDebounced: _.debounce(function () {
      this.getList();
    }, 300),

    updateWarehouseProductDebounced: _.debounce(function (id) {
      this.updateWarehouseProduct(id);
    }, 300),

    async getList() {
      this.loading = true;
      try {
        let params = {
          ...(this.sort ? { sort: this.sort } : {}),
          filter: {
            ...(this.query ? { search: encodeURIComponent(this.query) } : {}),
            warehouse: this.warehouse.id,
            sellingPriceMoreThanZero: true,
          },
          page: {
            number: this.pagination.currentPage,
            size: this.pagination.perPage,
          },
          include: "product,warehouse,unitOfMeasureUnit",
        };

        if (this.selectedSupplier) {
          params.filter.supplier = this.selectedSupplier;
        }
        if (this.selectedCategory) {
          params.filter.productInCategory = this.selectedCategory;
        }
        if (this.filterCustomPricing) {
          params.filter.customSellingPrice = true;
        }

        await this.$store.dispatch("warehouseProducts/list", params);
        this.warehouseProducts = await this.$store.getters[
          "warehouseProducts/list"
        ];

        this.total = this.$store.getters["warehouseProducts/listTotal"];
        this.loading = false;
        this.scrollToTop();
      } catch (error) {
        this.$notify({
          type: "danger",
          message: this.$t("ERRORS.SOMETHING_WENT_WRONG"),
        });
        this.loading = false;
      }
    },

    refreshWarehouseProducts() {
      const warehouseProductsModelData = {};
      for (const item of this.warehouseProducts) {
        warehouseProductsModelData[item.id] = _.cloneDeep(item);
        warehouseProductsModelData[item.id].loading = false;
      }
      this.warehouseProductsModel = warehouseProductsModelData;
    },

    onPriceChange(id) {
      const model = this.warehouseProductsModel[id];

      if (!model.custom_selling_price) {
        model.custom_selling_price = true;
      }

      this.updateWarehouseProductDebounced(id);
    },

    onSubsidizedPriceChange(id) {
      this.updateWarehouseProductDebounced(id);
    },

    onCatererPriceChange(id) {
      this.updateWarehouseProductDebounced(id);
    },

    async updateWarehouseProduct(id) {
      this.warehouseProductsModel[id].loading = true;
      const warehouseProductData = this.warehouseProductsModel[id];

      try {
        await this.$store.dispatch(
          "warehouseProducts/update",
          warehouseProductData
        );
        this.warehouseProductsModel[id].loading = false;
      } catch (error) {
        this.$notify({
          type: "danger",
          message: this.$t("ERRORS.SOMETHING_WENT_WRONG"),
        });
        this.warehouseProductsModel[id].loading = false;
      }
    },

    async resetCustomPricing(id) {
      const model = this.warehouseProductsModel[id];
      model.custom_selling_price = false;
      model.loading = true;

      try {
        await this.$store.dispatch("warehouseProducts/update", model);
        this.getList();
      } catch (error) {
        this.$notify({
          type: "danger",
          message: this.$t("ERRORS.SOMETHING_WENT_WRONG"),
        });
        model.loading = false;
      }
    },

    scrollToTop() {
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    },

    sortChange({ prop, order }) {
      if (order === "descending") {
        this.sort = `-${prop}`;
      } else {
        this.sort = `${prop}`;
      }
      this.getList();
    },

    async exportToExcel() {
      this.exporting = true;
      try {
        let params = {
          filter: {
            ...(this.query ? { search: encodeURIComponent(this.query) } : {}),
            warehouse: this.warehouse.id,
            sellingPriceMoreThanZero: true,
          },
        };

        if (this.selectedSupplier) {
          params.filter.supplier = this.selectedSupplier;
        }
        if (this.selectedCategory) {
          params.filter.productInCategory = this.selectedCategory;
        }

        const response = await this.$store.dispatch(
          "warehouseProducts/print",
          params
        );

        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `sales-pricing-${this.warehouse.name}.xlsx`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (error) {
        this.$notify({
          type: "danger",
          message: this.$t("ERRORS.SOMETHING_WENT_WRONG"),
        });
      }
      this.exporting = false;
    },
  },
};
</script>

<style>
.sales-pricing-list .el-table__row .form-group {
  margin-bottom: 0;
}
</style>
